import PropTypes from 'prop-types'
import React from 'react'
import { connect } from 'react-redux'
import { createSelector } from 'reselect'

import Button from 'components/Button'
import Comment from 'components/Comment'
import getIdReduxUtil from 'modules/redux/utils/get-id'
import {
  createCommentAction,
  delCommentAction,
  discardCommentAction,
  saveCommentAction,
  updateCommentAction
} from 'modules/comments/actions'
import { isAdminUserUtil } from 'models/daos/user/utils'
import isNewReduxUtil from 'modules/redux/utils/is-new'
import openModalAction from 'modules/modal/actions/open'
import { MODAL_ID } from 'modules/modal/constants/index.cjs'
import {
  COMMENT_MODEL_KEY,
  COMMENT_MODEL_VALIDATOR
} from 'models/daos/comment/constants'
import { getHtmlIdCommentUtil } from 'models/daos/comment/utils'
import {
  getCommentsSelector,
  getUserCommentSelector,
  isReportedByCurrentUserSelector
} from 'modules/comments/selectors'
import getCurrentUserSelector from 'modules/users/selectors/get-current'
import { STYLES_COLOR } from 'modules/styles/constants/index.cjs'
import { isEqualContentCollectionUtil } from 'utils/collection'
import { getObjectUtil } from 'packages/src'

class CommentContainer extends React.Component {
  constructor (props) {
    super(props)
    this.handleClickAnswer = this.handleClickAnswer.bind(this)
    this.handleClickDelete = this.handleClickDelete.bind(this)
    this.handleClickEdit = this.handleClickEdit.bind(this)
    this.handleClickCancel = this.handleClickCancel.bind(this)
    this.handleClickSave = this.handleClickSave.bind(this)
  }

  shouldComponentUpdate (nextProps) {
    return (
      this.props.instance !== nextProps.instance ||
      !isEqualContentCollectionUtil(this.props.comments, nextProps.comments)
    )
  }

  handleChange (value) {
    const id = getIdReduxUtil(this.props.instance)
    this.props.dispatch(
      updateCommentAction(
        id, value, this.props.associatedInstance
      )
    )
  }

  handleClick () {
    this.openLogInPage()
  }

  handleClickAnswer () {
    // If the current `User` is not logged in, show the SigninPage
    if (!this.props.currentUser) {
      this.openLogInPage()
      return
    }
    // Add a `Comment` to the current `Comment`
    const id = getIdReduxUtil(this.props.instance)
    this.props.dispatch(
      createCommentAction(
        id,
        this.props.associatedInstance,
        this.props.rootInstance
      )
    )
  }

  handleClickCancel () {
    const id = getIdReduxUtil(this.props.instance)
    this.props.dispatch(
      discardCommentAction(id, this.props.associatedInstance)
    )
  }

  handleClickDelete () {
    const id = getIdReduxUtil(this.props.instance)
    this.props.dispatch(
      delCommentAction(id, this.props.associatedInstance)
    )
  }

  handleClickEdit () {
    // Trigger an update so the `isChanged` is modified,
    // which will set `isEditing` on the `<Comment>`
    const id = getIdReduxUtil(this.props.instance)
    const value = this.getValue()
    this.props.dispatch(
      updateCommentAction(id, value)
    )
  }

  handleClickSave () {
    const id = getIdReduxUtil(this.props.instance)
    this.props.dispatch(
      saveCommentAction(id, this.props.associatedInstance)
    )
  }

  getValue () {
    return (
      getObjectUtil(this.props.instance, 'data.value') ||
      getObjectUtil(this.props.instance, 'changed.value')
    )
  }

  hasChanged () {
    return getObjectUtil(this.props.instance, 'changed.value') != null
  }

  isCurrentUserAdmin () {
    return isAdminUserUtil(this.props.currentUser)
  }

  isEditable () {
    const userId = getObjectUtil(this.props.user, 'data.id')
    const currentUserId = getObjectUtil(this.props.currentUser, 'data.id')
    return (
      userId === currentUserId
    )
  }

  isNew () {
    return isNewReduxUtil(this.props.instance)
  }

  isEditing () {
    return this.hasChanged() || this.isNew()
  }

  openLogInPage () {
    this.props.dispatch(openModalAction(MODAL_ID.SIGNIN))
  }

  renderAnswer () {
    if (this.isEditable() || this.isNew()) {
      return
    }
    return (
      <Button onClick={this.handleClickAnswer}>
        Answer
      </Button>
    )
  }

  renderDelete () {
    if (
      this.isEditing() ||
      !this.isEditable() ||
      (
        this.isEditable() &&
        !this.isCurrentUserAdmin()
      ) ||
      this.isNew()
    ) {
      return
    }
    return (
      <Button
        color={Button.COLOR.RED}
        onClick={this.handleClickDelete}
      >
        Delete
      </Button>
    )
  }

  renderEdit () {
    if (
      !this.isEditable() ||
      this.isEditing()
    ) {
      return
    }
    return (
      <Button onClick={this.handleClickEdit}>
        Edit
      </Button>
    )
  }

  renderCancel () {
    if (!this.hasChanged()) {
      return
    }
    return (
      <Button onClick={this.handleClickCancel}>
        Cancel
      </Button>
    )
  }

  renderSave () {
    if (!this.hasChanged()) {
      return
    }
    const { instance } = this.props
    const comment = getObjectUtil(instance, 'changed.value')
    const isDisabled = (
      comment.length < COMMENT_MODEL_VALIDATOR[COMMENT_MODEL_KEY.VALUE][0] ||
      comment.length > COMMENT_MODEL_VALIDATOR[COMMENT_MODEL_KEY.VALUE][1]
    )
    const isPublish = this.isNew()
    return (
      <Button
        isDisabled={isDisabled}
        onClick={this.handleClickSave}
        style={{
          color: STYLES_COLOR.BLUE
        }}
      >
        {isPublish ? 'Publish' : 'Save'}
      </Button>
    )
  }

  renderActions () {
    if (this.isNew() && !this.hasChanged()) {
      return
    }
    return (
      <Comment.Buttons>
        {this.renderCancel()}
        {this.renderSave()}
        {this.renderDelete()}
        {this.renderAnswer()}
        {this.renderEdit()}
      </Comment.Buttons>
    )
  }

  renderComment () {
    const isFullWidth = !isNewReduxUtil(this.props.instance)
    return (
      <div
        style={{
          width: isFullWidth ? '100%' : undefined,
          flexGrow: '1',
          flexShrink: '1'
        }}
      >
        {this.renderText()}
        {this.renderActions()}
        {this.renderComments()}
      </div>
    )
  }

  renderComments () {
    const { comments } = this.props
    if (!comments.length) {
      return
    }
    return (
      <>
        {
          comments.map((comment, i) => (
            <ConnectedCommentContainer
              associatedInstance={this.props.instance}
              key={i}
              instance={comment}
              rootInstance={this.props.associatedInstance}
            />
          ))
        }
      </>
    )
  }

  renderDate () {
    const { instance } = this.props
    const createdAt = getObjectUtil(instance, 'data.createdAt')
    const updatedAt = getObjectUtil(instance, 'data.updatedAt')
    if (!createdAt) {
      return
    }
    const props = {
      value: createdAt,
      wasEdited: createdAt && createdAt !== updatedAt
    }
    return <Comment.Date {...props} />
  }

  renderText () {
    const comment = this.getValue()
    const currentUserId = getObjectUtil(this.props.currentUser, 'data.id')
    return (
      <Comment.Text
        onChange={
          currentUserId && this.isEditing() ? this.handleChange.bind(this) : null
        }
        onClick={
          !currentUserId && !comment ? this.handleClick.bind(this) : null
        }
        value={comment}
      />
    )
  }

  renderUser () {
    const comment = this.getValue()
    const props = {
      comment,
      value: this.props.user,
      currentUser: this.props.currentUser,
      isNew: isNewReduxUtil(comment)
    }
    return <Comment.User {...props} />
  }

  render () {
    const { instance } = this.props
    const htmlId = instance ? getHtmlIdCommentUtil(instance) : null
    const isReported = this.props.isReportedByCurrentUser
    return (
      <Comment
        htmlId={htmlId}
        isReported={isReported}
      >
        {this.renderUser()}
        {this.renderDate()}
        {this.renderComment()}
      </Comment>
    )
  }
}

CommentContainer.propTypes = {
  dispatch: PropTypes.func.isRequired,
  user: PropTypes.object,
  currentUser: PropTypes.object,
  instance: PropTypes.object,
  comments: PropTypes.arrayOf(
    PropTypes.object
  ),
  associatedInstance: PropTypes.object,
  rootInstance: PropTypes.object,
  isReportedByCurrentUser: PropTypes.bool.isRequired
}

const select = () => createSelector(
  getUserCommentSelector(),
  getCommentsSelector(),
  isReportedByCurrentUserSelector(),
  getCurrentUserSelector(),
  (
    user,
    comments,
    isReportedByCurrentUser,
    currentUser
  ) => ({
    user,
    comments,
    isReportedByCurrentUser,
    currentUser
  })
)
const ConnectedCommentContainer = connect(select)(CommentContainer)
export default ConnectedCommentContainer
